<template>
  <div class="index container">
    <transition name="fade">
      <router-view @showParent="showParent"> </router-view>
    </transition>

    <div v-if="showParentPage">
      <v-breadcrumbs>
        <v-breadcrumbs-item :exact="true" :to="{ name: 'home', params: {} }"
          >Home</v-breadcrumbs-item
        >
        <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
        <v-breadcrumbs-item
          :exact="true"
          :to="{ name: 'requirement', params: {} }"
          active
        >
          Requirement
        </v-breadcrumbs-item>
      </v-breadcrumbs>

      <div class="d-flex flex-column flex-lg-row">
        <v-text-field
          dense
          v-model="searchTerms"
          @keyup.enter="search"
          @click:clear="clearSearch"
          @click:append="search"
          placeholder="Press enter to begin search"
          append-icon="mdi-magnify"
          outlined
          clearable
          label="Search"
        />
        <div class="d-flex flex-row">
          <v-select
            @change="search"
            label="Filter Target"
            v-model="filterTarget"
            class="ml-md-4"
            :style="`${$vuetify.breakpoint.mdAndUp ? 'max-width: 200px;' : ''}`"
            dense
            outlined
            :items="contactTypes"
            item-value="id"
            item-text="name"
          />
          <v-btn
            class="ml-4"
            color="primary"
            :to="{ name: 'requirementCreate', params: {} }"
          >
            Create Requirement
          </v-btn>
        </div>
      </div>

      <div class="table-container">
        <v-data-table
          :headers="headers"
          :items="requirementList"
          :items-per-page="pagination.pageSize"
          :loading="!requirementProcessed"
          :server-items-length="pagination.totalSize"
          :page="pagination.current"
          :disable-pagination="!requirementList || requirementList.length < 1"
          :hide-default-header="!requirementList || requirementList.length < 1"
          :hide-default-footer="!requirementList || requirementList.length < 1"
          :options.sync="options"
          item-key="id"
          class="elevation-1"
          @update:options="pageChanged"
        >
          <template v-slot:item.name="{ item }">
            <a @click="view(item)"> {{ item.name }}</a>
          </template>

          <template v-slot:item.target="{ item }">
            {{ getContactTypeName(item.target) }}
          </template>

          <template v-slot:item.required="{ item }">
            {{ item.required ? "Yes" : "No" }}
          </template>

          <template v-slot:item.attachmentRequired="{ item }">
            {{ item.attachmentRequired ? "Yes" : "No" }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon>
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list class="pa-2" width="200px">
                <v-list-item @click="openClone(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-content-duplicate</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content> Clone </v-list-item-content>
                </v-list-item>
                <v-list-item @click="edit(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-pencil</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content> Edit </v-list-item-content>
                </v-list-item>
                <v-list-item @click="remove(item)">
                  <v-list-item-icon
                    ><v-icon>mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content> Delete </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </div>
    </div>

    <v-dialog v-if="selectedItem" v-model="cloneMode">
      <v-card>
        <v-card-title class="justify-end">
          <div>
            <v-btn @click="cloneMode = null" icon
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </div>
        </v-card-title>
        <v-card-subtitle>
            Choose a target to clone this requirement ({{selectedItem.name}}) to
        </v-card-subtitle>
        <v-card-text >

          <v-select
            label="Target"
            v-model="cloneTarget"
            outlined
            :items="cloneTargets"
            item-value="id"
            item-text="name"
          />
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn class="my-4" @click="clone"> Clone </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirmation-dialog ref="confirm" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { getAllContactTypes } from "@/services/contactType";

const ConfirmationDialog = () =>
  import("@/components/common/ConfirmationDialog.vue");

export default {
  data() {
    return {
      pagination: {
        current: 1,
        pageSize: 10,
        totalSize: 100,
      },
      options: {
        //define default sorting option here.
        sortBy: [],
        sortDesc: [],
      },
      requirementList: [],
      showParentPage: true,
      filterTarget: null,
      contactTypes: [],
      cloneMode: false,
      cloneTarget: null,
      selectedItem: null,
      headers: [
        {
          text: "Name",
          value: "name",
          align: "start",
          sortable: true,
        },
        {
          text: "Description",
          value: "description",
          align: "start",
          sortable: false,
        },
        {
          text: "Target",
          value: "target",
          align: "start",
          sortable: true,
        },
        {
          text: "Requirement Type",
          value: "type",
          align: "start",
          sortable: true,
        },
        {
          text: "Required",
          value: "required",
          align: "start",
          sortable: false,
        },
        {
          text: "Require Proof",
          value: "attachmentRequired",
          align: "start",
          sortable: false,
        },

        { text: "Actions", value: "actions", align: "end", sortable: false },
      ],
      sort: {
        key: null,
      },
      searchTerms: "",
    };
  },
  components: {
    ConfirmationDialog,
  },
  computed: {
    ...mapGetters("requirement", ["requirementProcessed", "getRequirements"]),
    cloneTargets(){
      if(this.contactTypes){
        return this.contactTypes.filter(t => t.id > 0);
      }
      else{
        return []
      }
    }
  },
  methods: {
    ...mapActions("messages", [
      "addErrorMessage",
      "addMessage",
      "addSuccessMessage",
    ]),
    ...mapActions("requirement", ["deleteRequirement", "fetchRequirements"]),
    view(item) {
      this.$router.push({ name: "requirementView", params: { id: item.id } });
    },
    edit(item) {
      this.$router.push({ name: "requirementEdit", params: { id: item.id } });
    },
    async remove(item) {
      let confirm = await this.$refs.confirm.open(
        "Confirm Delete",
        "Do you want to delete this requirement? This will also remove all submissions with this requirement."
      );
      if (confirm) {
        this.deleteRequirement(item.id)
          .then(() => {
            this.addSuccessMessage("requirement was deleted successfully");
            this.getResources();
          })
          .catch(() => {
            this.addErrorMessage("Server response with error ");
          });
      }
    },
    getResources() {
      //default to sorting by ID desc if not defined
      const sortBy =
        this.options["sortBy"] && this.options["sortBy"].length > 0
          ? this.options.sortBy[0]
          : "id";
      const sortDesc =
        this.options["sortDesc"] && this.options["sortDesc"].length > 0
          ? this.options.sortDesc[0]
          : true;
      const params = {
        page:
          this.pagination.pageIndex && this.pagination.pageIndex > 0
            ? this.pagination.pageIndex - 1
            : 0,
        size: this.pagination.pageSize,
        sort: `${sortBy}${sortDesc ? ",desc" : ""}`,
        name: this.searchTerms,
        target: this.filterTarget,
      };
      this.fetchRequirements(params)
        .then((response) => {
          if (response) {
            this.pagination.totalSize = response.totalElements;
            this.requirementList = response.content;
          }
        })
        .catch((error) => {
          this.addErrorMessage("Server response with error " + error);
        });
    },
    showParent(show) {
      this.showParentPage = show;
    },
    pageChanged(pagination) {
      this.pagination.pageIndex = pagination.page;
      this.pagination.pageSize = pagination.itemsPerPage;

      this.getResources();
    },
    search() {
      this.pagination.pageIndex = 0;
      this.pagination.current = 1;
      this.getResources();
    },
    clearSearch() {
      this.searchTerms = null;
      this.getResources();
    },
    getContactTypeName(id) {
      const contactType = this.contactTypes.find((c) => c.id == id);
      return contactType ? contactType.name : "n/a";
    },
    openClone(item){
      this.selectedItem = item;
      this.cloneMode = true;
    },
    clone(){
      this.$axios.post('/requirement/clone', null, {params: {src: this.selectedItem.id, target: this.cloneTarget}})
        .then((resp) => {
          const saved = resp.data;
          this.view(saved)
        })
        .catch( (error) => {
          this.addErrorMessage("Error cloning requirement; " + error.data.message)
        })
    }
  },
  created() {
    getAllContactTypes({ page: 0, size: 1000 }).then((response) => {
      this.contactTypes = response.data.content;
      this.contactTypes.unshift({ id: null, name: "All Audiences" });
    });
  },
  watch: {
    "pagination.numberRegisterForPage": function () {
      this.pagination.current = -1;
    },
  },
  beforeRouteUpdate(to, from, next) {
    const toDepth = to.path.split("/").length;
    const fromDepth = from.path.split("/").length;
    this.showParentPage = toDepth < fromDepth;
    if (this.showParentPage) {
      this.getResources();
    }
    next();
  },
};
</script>
